// GelatoPrice.js (updated to include gelatoProductId in price response & add-to-cart payload)
import React, { useEffect, useState, useRef } from "react";
import * as htmlToImage from "html-to-image";
import { captureThumbnailPng, generatePdfFromDomUsingInches, buildUploadFormData, dataURLToBlob  } from '../utils/ExportUtils';
import "../style/GelatoPricingBar.css";

// IMPORTANT: Canvas must be available in the same folder (adjust path if needed)
import Canvas from "./Canvas";

const GelatoPricingBar = ({
  frameSettings,
  step,
  // REQUIRED: function that returns the design payload (layers, settings, etc)
  getGraphicPayload,
  // OPTIONAL: function(key, storageObject) to save design locally (if you already have one)
  saveGraphicLocally,
  // OPTIONAL: pass canvas props so the modal can render the same live Canvas (recommended)
  canvasProps = null,
  setLoading,
}) => {
  const [price, setPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [adding, setAdding] = useState(false);

  // NEW: gelato product id received from price API
  const [gelatoProductId, setGelatoProductId] = useState(null);

  // modal / preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [modalAdding, setModalAdding] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const pendingDesignRef = useRef(null); // store payload + meta between confirm step

  const DEFAULT_EXCLUDE_SELECTORS = ['.vertical-dimension', '.horizontal-dimension', '.corner', '.zoom-toggle', '.dashed-border', '.preview-header', '.preview-footer'];

  useEffect(() => {
    if (step !== 5) return;

    const fetchPrice = async () => {
      try {
        setLoadingPrice(true);
        const { frame, size, orientation, material } = frameSettings || {};

        const response = await fetch(
          `https://rotateadate.com/wp-json/gelato/v1/price?frame=${encodeURIComponent(frame)}&size=${encodeURIComponent(size)}&orientation=${encodeURIComponent(orientation)}&material=${encodeURIComponent(material)}&country=US&currency=USD&quantity=1`
        );

        if (!response.ok) {
          console.error("Failed to fetch price", response.status);
          setPrice(null);
          setGelatoProductId(null);
          return;
        }

        const data = await response.json();
        console.log("Fetched price data:", data.final_price);

        // set price (ensure numeric)
        setPrice(typeof data?.final_price === "number" ? data.final_price : null);

        // NEW: set gelato product id if returned by backend
        if (data && data.gelato_product_id) {
          setGelatoProductId(String(data.gelato_product_id));
          console.log("GelatoPrice: received gelato_product_id:", data.gelato_product_id);
        } else {
          setGelatoProductId(null);
        }
      } catch (err) {
        console.error("Error fetching price:", err);
        setPrice(null);
        setGelatoProductId(null);
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchPrice();
  }, [frameSettings, step]);

  if (step !== 5) return null;

  /* ========= helpers (kept identical to your original implementation) ========= */

  const resizeDataUrl = (dataUrl, targetWidth = 200) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const ratio = img.height / img.width;
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = Math.round(targetWidth * ratio);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (e) => reject(e);
      img.src = dataUrl;
    });

  // Capture the current visible .frame-container (keeps background etc)
  const createThumbnailFromDom = async (selector = ".preview-canvas-area .frame-container") => {
    // use the same exclude selectors as PdfExportButton
    const excludeSelectors = DEFAULT_EXCLUDE_SELECTORS;
    // capture PNG with fixed width 200px
    const thumb = await captureThumbnailPng(selector, {
      targetWidthPx: 200,
      excludeSelectors,
      cacheBust: true
    });
    return thumb;
  };

  
// NEW: capture high-res image (for print) using html-to-image pixelRatio
// we force 300 DPI in the util; caller no longer needs to supply scaled DPI
const createHighResFromDom = async () => {
  const frameSizeInches = (frameSettings && (frameSettings.size || `${frameSettings.frame || 12}x${frameSettings.orientation || 16}`)) || '12x16';
  const excludeSelectors = DEFAULT_EXCLUDE_SELECTORS;
  const selector = frameSettings?.material === 'canvas' ? ".preview-canvas-area .frame-container" : ".preview-canvas-area .canvas";
  console.log("GelatoPricing: creating high-res print PDF for size", frameSizeInches, "selector", selector);
  console.log("GelatoPricing: frameSettings", frameSettings);
  const pdfDataUrl = await generatePdfFromDomUsingInches(
    selector,
    frameSizeInches,
    300,   // value ignored by util but keep for compatibility
    excludeSelectors,
    true,  // cover (util forces cover anyway)
    true,
    canvasProps?.otherSettings?.backgroundColor || '#ffffff',
    frameSettings?.material || 'paper'
  );

  return pdfDataUrl;
};



  const postJsonSafe = async (url, payload, nonce = null) => {
    try {
      const resp = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(nonce ? { "X-WP-Nonce": nonce } : {})
        },
        body: JSON.stringify(payload)
      });

      const text = await resp.text();
      let json = null;
      if (text && text.trim()) {
        try {
          json = JSON.parse(text);
        } catch (e) {
          return { ok: resp.ok, status: resp.status, text, json: null };
        }
      }
      return { ok: resp.ok, status: resp.status, text, json };
    } catch (err) {
      return { ok: false, status: 0, text: String(err), json: null, error: err };
    }
  };

  const ensureSession = async () => {
    try {
      await fetch("/cart", { method: "GET", credentials: "same-origin" });
      await new Promise((r) => setTimeout(r, 250));
    } catch (e) {
      console.warn("Session init failed:", e);
    }
  };

  /* ========= new UX: open modal first, perform capture + add only on confirm ========= */

  // User clicks "Add to Cart" button -> open the full-screen modal (do NOT call the add-to-cart server calls here)
  const openPreviewModal = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    console.log("GelatoPricing: openPreviewModal called");
    // reset modal controls
    setConfirmChecked(false);
    setModalAdding(false);
    setPreviewOpen(true);
    // keep global `adding` unchanged until user confirms
  };

  // User cancels preview -> close modal
  const cancelPreview = () => {
    console.log("GelatoPricing: cancelPreview called");
    setPreviewOpen(false);
    setModalAdding(false);
    setConfirmChecked(false);
  };

  // Confirm from modal -> do the full add-to-cart flow (this is your original onAddToCart code)
  const confirmAddToCart = async () => {
  if (modalAdding) return;
  if (!confirmChecked) {
    console.warn("confirmAddToCart: confirm checkbox not checked");
    return;
  }
  setLoading(true);
  setModalAdding(true);
  setAdding(true); // keep original global adding flag so the page button disables as well

  try {
    // 1) Get payload (use the same getGraphicPayload prop)
    if (typeof getGraphicPayload !== "function") {
      console.error("confirmAddToCart: getGraphicPayload is not a function. Ensure parent passes this prop.");
      throw new Error("Design payload function not available.");
    }
    const payload = await getGraphicPayload();
    if (!payload) throw new Error("Design payload not available. Pass getGraphicPayload prop.");

    // 2) capture thumbnail from DOM (ensures backgrounds are preserved)
    const thumbDataUrl = await createThumbnailFromDom(".preview-canvas-area .frame-container");

    // 2.5 attempt to generate a high-res print image:
    let highresDataUrl = "";
    try {
      // pick scale heuristically based on payload.svgSize if available
      const svgSize = payload?.svgSize || 800;
     let scale = 3;
    if (svgSize >= 1200) scale = 4;
    if (svgSize >= 2000) scale = 6;
    scale = Math.max(2, Math.min(6, scale));
      highresDataUrl = await createHighResFromDom(".preview-canvas-area .canvas");
      console.log("GelatoPricing: highres length:", highresDataUrl ? highresDataUrl.length : 0);
    } catch (e) {
      console.warn("High-res capture failed, proceeding without it:", e);
      highresDataUrl = "";
    }

    // 3) persist locally
    const designKey = `spirogift_design_${Date.now()}`;
    const storageObject = {
      key: designKey,
      createdAt: new Date().toISOString(),
      payload,
      thumbnail: thumbDataUrl,
      meta: { frameSettings, price }
    };

    if (typeof saveGraphicLocally === "function") {
      try {
        await saveGraphicLocally(designKey, storageObject);
      } catch (err) {
        console.warn("saveGraphicLocally failed", err);
        try { localStorage.setItem(designKey, JSON.stringify(storageObject)); } catch (e) { console.warn('localStorage save failed', e); }
      }
    } else {
      try { localStorage.setItem(designKey, JSON.stringify(storageObject)); } catch (err) { console.warn("Could not write to localStorage:", err); }
    }

    // 4) call plugin endpoints (same flow) — include highres fields now
    const endpoints = [
      "/wp-json/gelato/v1/create-dynamic-product",
      "/wp-json/gelato-connect/v1/create-dynamic-product",
      "/wp-json/gelatoconnect/v1/create-dynamic-product"
    ];

    const nonce =
      (window.gelatoConnect && window.gelatoConnect.nonce) ||
      (window.wpApiSettings && window.wpApiSettings.nonce) ||
      null;

    // highres filename and folder hint
    const highresFilename = `${designKey}_highres.pdf`;

    // Build the fallback JSON body (used when highresDataUrl is empty)
    const jsonBody = {
      design_key: designKey,
      design: payload,
      thumbnail: thumbDataUrl,
      meta: {
        price: price || 0,
        frameSettings,
        source: "react_app"
      },
      // NEW: highres payload + filename + save folder hint
      highres: highresDataUrl || "",
      highres_filename: highresFilename,
      save_folder: "gc_highres",
      // NEW: pass gelato product id (if we received it earlier from price call)
      gelato_product_id: gelatoProductId || null
    };

    console.log("GelatoPricing: add-to-cart body lengths:", {
      thumbnail_len: jsonBody.thumbnail ? jsonBody.thumbnail.length : 0,
      highres_len: jsonBody.highres ? (jsonBody.highres.length) : 0,
      highres_filename: jsonBody.highres_filename,
      gelato_product_id: jsonBody.gelato_product_id
    });

    // helper: convert dataURL -> Blob for multipart upload
    function dataURLToBlob(dataURL) {
      if (!dataURL) return null;
      const parts = dataURL.split(',');
      if (parts.length < 2) return null;
      const meta = parts[0];
      const b64 = parts.slice(1).join(',');
      const mimeMatch = meta.match(/data:([^;]+);/);
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const binary = atob(b64);
      const len = binary.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) arr[i] = binary.charCodeAt(i);
      return new Blob([arr], { type: mime });
    }

    // helper: perform multipart/form-data POST and return a result shape similar to postJsonSafe
    async function postMultipartSafe(url, formData, nonceHeader = null) {
      try {
        const headers = {};
        if (nonceHeader) headers['X-WP-Nonce'] = nonceHeader;
        // do not set Content-Type; browser will set boundary
        const resp = await fetch(url, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers
        });

        const text = await resp.text().catch(() => '');
        let json = null;
        try { json = text ? JSON.parse(text) : null; } catch (e) { json = null; }

        return { ok: resp.ok, status: resp.status, json, text };
      } catch (err) {
        console.error("postMultipartSafe error:", err);
        return { ok: false, status: 0, json: null, text: String(err) };
      }
    }

    let finalJson = null;
    let gotOk = false;

    // If we have highresDataUrl, prefer multipart upload; otherwise use existing JSON flow (postJsonSafe)
    for (let i = 0; i < endpoints.length; i++) {
      const url = endpoints[i];

      let result;
      if (highresDataUrl) {
        // build FormData preserving your existing fields
        const form = new FormData();

        // Append fields mirroring 'body' JSON keys
        form.append('design_key', designKey);
        // send design as JSON string so server can decode
        form.append('design', JSON.stringify(payload));
        form.append('thumbnail', thumbDataUrl);
        form.append('meta', JSON.stringify({
          price: price || 0,
          frameSettings,
          source: "react_app"
        }));

        // file: highres PDF blob
        try {
          const blob = dataURLToBlob(highresDataUrl);
          if (blob) {
            form.append('highres_file', blob, highresFilename);
          } else {
            // fallback to include base64 if conversion fails (shouldn't happen)
            form.append('highres', highresDataUrl || "");
          }
        } catch (e) {
          console.warn("Failed to convert highres dataURL to Blob, sending as base64 fallback:", e);
          form.append('highres', highresDataUrl || "");
        }

        // other hints / fields retained
        form.append('highres_filename', highresFilename);
        form.append('save_folder', 'gc_highres');
        if (gelatoProductId) form.append('gelato_product_id', String(gelatoProductId));
        if (frameSettings && frameSettings.size) form.append('frame_size', String(frameSettings.size));

        // send multipart
        result = await postMultipartSafe(url, form, nonce);
      } else {
        // existing JSON POST flow (unchanged) using postJsonSafe
        result = await postJsonSafe(url, jsonBody, nonce);
      }

      if (result.json && result.json.success) {
        finalJson = result.json;
        gotOk = true;
        console.log("GelatoPricing: add-to-cart succeeded at", url, finalJson);
        break;
      }

      console.warn("Add-to-cart attempt:", url, result);

      const serverMessage = result.json && result.json.message ? String(result.json.message) : String(result.text || "");
      if (/could not add to cart/i.test(serverMessage)) {
        console.info("Server indicated add_to_cart failed (likely missing session). Initializing session and retrying once.");
        await ensureSession();
        // retry once with same method (multipart or json)
        let retryResult;
        if (highresDataUrl) {
          // rebuild identical FormData for retry
          const retryForm = new FormData();
          retryForm.append('design_key', designKey);
          retryForm.append('design', JSON.stringify(payload));
          retryForm.append('thumbnail', thumbDataUrl);
          retryForm.append('meta', JSON.stringify({
            price: price || 0,
            frameSettings,
            source: "react_app"
          }));
          try {
            const blob = dataURLToBlob(highresDataUrl);
            if (blob) retryForm.append('highres_file', blob, highresFilename);
            else retryForm.append('highres', highresDataUrl || "");
          } catch (e) {
            retryForm.append('highres', highresDataUrl || "");
          }
          retryForm.append('highres_filename', highresFilename);
          retryForm.append('save_folder', 'gc_highres');
          if (gelatoProductId) retryForm.append('gelato_product_id', String(gelatoProductId));
          if (frameSettings && frameSettings.size) retryForm.append('frame_size', String(frameSettings.size));

          retryResult = await postMultipartSafe(url, retryForm, nonce);
        } else {
          retryResult = await postJsonSafe(url, jsonBody, nonce);
        }

        if (retryResult.json && retryResult.json.success) {
          finalJson = retryResult.json;
          gotOk = true;
          console.log("GelatoPricing: retry add-to-cart succeeded at", url, retryResult.json);
          break;
        }
      }
    }

    if (!gotOk || !finalJson) {
      console.error("Add-to-cart failed after attempts. Last response:", finalJson);
      throw new Error((finalJson && finalJson.message) || "Server failed to add to cart");
    }

    if (finalJson.attachment_url) {
      try { localStorage.setItem(`${designKey}_thumb_url`, finalJson.attachment_url); } catch (e) { /* ignore quota */ }
    }

    // if server returned highres_url or highres_saved flag, store it locally too
    if (finalJson.highres_url) {
      console.log("GelatoPricing: server saved highres at", finalJson.highres_url);
      try { localStorage.setItem(`${designKey}_highres_url`, finalJson.highres_url); } catch (e) { /* ignore */ }
    }

    // server may return product id confirmation; show in console
    if (finalJson.gelato_product_id) {
      console.log("GelatoPricing: server acknowledged gelato_product_id:", finalJson.gelato_product_id);
    }

    // Redirect to cart (same as before)
    if (finalJson.cart_url) {
      window.location.href = finalJson.cart_url;
      return;
    }
    if (finalJson.cart_key) {
      window.location.href = `/cart?edit_cart_key=${encodeURIComponent(finalJson.cart_key)}`;
      return;
    }
    window.location.href = "/cart";
  } catch (err) {
    console.error("Add to cart failed:", err);
    alert("Failed to add to cart: " + (err.message || err));
  } finally {
    setModalAdding(false);
    setAdding(false);
    pendingDesignRef.current = null;
    setPreviewOpen(false);
    setConfirmChecked(false);
    setLoading(false);
  }
};


  /* ========= Modal component (full-screen) ========= */

  const PreviewModal = () => {
    /* Replace the old inline-styled modal with this markup */
    if (!previewOpen) return null;

    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Preview design before adding to cart"
        className="preview-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget) cancelPreview(); }}
      >
        <div className="preview-container canvas-container frame-container">
          <header className="preview-header">
            <strong className="preview-title">Preview your design</strong>
            <button
              aria-label="Close preview"
              onClick={cancelPreview}
              className="preview-close"
              type="button"
            >
              ✕
            </button>
          </header>

          <section className="preview-body">
            {canvasProps ? (
              <div className="preview-canvas-area">
                {/* Render Canvas in try/catch so modal doesn't crash when Canvas errors */}
                <CanvasWrapper canvasProps={canvasProps} />
              </div>
            ) : (
              <div className="preview-fallback">
                <span>Canvas preview unavailable — pass canvasProps to GelatoPricing to show live preview.</span>
              </div>
            )}
          </section>

          <footer className="preview-footer">
            <div className="preview-confirm-left">
              <label className="checkbox-wrapper">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
                <span className="checkbox-custom" />
              </label>

              <div className="confirm-text">
                I confirm I have reviewed the graphic and it is correct. I understand this design will be used for the final product.
              </div>
            </div>

            <div className="preview-actions">
              <button
                type="button"
                className={`btn ${modalAdding || !confirmChecked ? 'disabled' : ''}`}
                onClick={confirmAddToCart}
                disabled={modalAdding || !confirmChecked}
              >
                {modalAdding ? "Adding…" : "Confirm"}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelPreview}
                disabled={modalAdding}
              >
                Cancel
              </button>
            </div>
          </footer>
        </div>
      </div>
    );

  };

  // minimal Canvas wrapper to prevent render crash
  const CanvasWrapper = ({ canvasProps }) => {
    try {
      return <Canvas {...canvasProps} />;
    } catch (err) {
      console.error("Canvas render error in preview modal:", err);
      return (
        <div style={{ padding: 20 }}>
          <strong>Preview failed to render.</strong>
          <div>Please ensure the canvas is available and try again.</div>
        </div>
      );
    }
  };

  /* ========= render main bar (button now opens preview modal) ========= */

  return (
    <>
      <div className="gelato-pricing-bar">
        <div className="pricing-left">
          <span className="cart-icon">🛒</span>
        </div>

        <div className="pricing-center">
          {loadingPrice && <span className="price-text">Loading...</span>}

          {!loadingPrice && price !== null && (
            <>
              {/* <span className="old-price">$69.99</span> */}
              <span className="new-price">${Number(price).toFixed(2)}</span>
            </>
          )}

          {!loadingPrice && price === null && (
            <span className="price-text">Price unavailable</span>
          )}
        </div>

        <div className="pricing-right">
          <button
            type="button"                          // <- explicit type to avoid accidental form submits
            className="add-to-cart-btn"
            onClick={openPreviewModal}         // <-- opens modal instead of running add-to-cart directly
            disabled={adding || loadingPrice || price === null}
            title={price === null ? "Price unavailable" : "Add design to cart"}
          >
            {adding ? "Processing…" : "Add to Cart →"}
          </button>
        </div>
      </div>

      {/* full-screen preview modal */}
      <PreviewModal />
    </>
  );
};

export default GelatoPricingBar;
